import {
  CheckboxField,
  DatePickerField,
  DropdownField,
  Flex,
  isFutureDate,
  RadioField,
  TextAreaField,
} from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useIntl } from "react-intl";
import { getBankAccountMasksAsString } from "shared-ui";

import { AIR_MESSAGES } from "../../message-definitions";
import type {
  BankStatementUploadsFormFields,
  UnableToProvideBankStatementFormOptions,
} from "../../types/bank-statements";

import { MESSAGES } from "./message-definitions";

dayjs.extend(LocalizedFormat);
dayjs.extend(utc);

interface ConfirmBankStatementFieldsProps {
  form: UseFormReturn<BankStatementUploadsFormFields>;
  airType: FundingApplicationSchemas.AdditionalInformationRequestType;
  bankName?: string | null;
  bankAccountMasks?: string[];
  setUnableToProvideStatements: (value: boolean) => void;
  unableToProvideStatements: boolean | null;
  setUnableToSendStatmentsReasonsValid: (valid: boolean) => void;
}

export const ConfirmBankStatementFields = ({
  form,
  airType,
  bankName,
  bankAccountMasks,
  setUnableToSendStatmentsReasonsValid,
  unableToProvideStatements,
  setUnableToProvideStatements,
}: ConfirmBankStatementFieldsProps) => {
  const intl = useIntl();

  const {
    unable_to_provide_statements_reason: unableToProvideStatementsReason,
    unable_to_provide_statements_reason_other: unableToProvideStatementsReasonOther,
    unable_to_provide_statements_reason_open_date: unableToProvideStatementsReasonOpenDate,
    unable_to_provide_statements_reason_close_dates: unableToProvideStatementsReasonCloseDate,
  } = form.watch();

  // Todo: Used to differentiate between copy
  // Most copy can be replaced by the plural supported version once translations are added
  const hasStatementsForMultipleAccounts = (bankAccountMasks?.length ?? 0) > 1;

  const unableToSendStatmentsReasonOtherValid =
    unableToProvideStatementsReason === "other" &&
    unableToProvideStatementsReasonOther !== "" &&
    unableToProvideStatementsReasonOther !== null;
  const unableToSendStatmentsReasonClosedValid =
    unableToProvideStatementsReason === "closed" &&
    (unableToProvideStatementsReasonCloseDate?.length ?? 0) > 0 &&
    unableToProvideStatementsReasonCloseDate?.every(
      ({ closeDate }) => closeDate && closeDate <= dayjs().format("YYYY-MM-DD")
    );
  const unableToSendStatmentsReasonOpenedTooRecentlyValid =
    unableToProvideStatementsReason === "opened_too_recently" &&
    unableToProvideStatementsReasonOpenDate &&
    unableToProvideStatementsReasonOpenDate <= dayjs().format("YYYY-MM-DD");

  const unableToProvideStatementsOptions: UnableToProvideBankStatementFormOptions[] = (() => {
    const baseOptions: UnableToProvideBankStatementFormOptions[] = [
      {
        id: "closed",
        text: hasStatementsForMultipleAccounts
          ? intl.formatMessage(MESSAGES.bankStatementUnableToProvideReasonClosedMultipleMasks)
          : intl.formatMessage(MESSAGES.bankStatementUnableToProvideReasonClosed),
      },
      {
        id: "opened_too_recently",
        text: hasStatementsForMultipleAccounts
          ? intl.formatMessage(MESSAGES.bankStatementUnableToProvideReasonOpenedTooRecentlyMultipleMasks)
          : intl.formatMessage(MESSAGES.bankStatementUnableToProvideReasonOpenedTooRecently),
      },
      {
        id: "other",
        text: intl.formatMessage(MESSAGES.bankStatementUnableToProvideReasonOther),
      },
    ];

    if (airType === "reconnect_connection") {
      return baseOptions.filter((option) => option.id !== "opened_too_recently");
    }
    return baseOptions;
  })();

  useEffect(() => {
    const isUnableToSendStatmentsReasonsValid = !!(
      unableToProvideStatements &&
      unableToProvideStatementsReason &&
      (unableToSendStatmentsReasonOtherValid ||
        unableToSendStatmentsReasonClosedValid ||
        unableToSendStatmentsReasonOpenedTooRecentlyValid)
    );
    setUnableToSendStatmentsReasonsValid(isUnableToSendStatmentsReasonsValid);
  }, [
    setUnableToSendStatmentsReasonsValid,
    unableToProvideStatements,
    unableToProvideStatementsReason,
    unableToSendStatmentsReasonClosedValid,
    unableToSendStatmentsReasonOpenedTooRecentlyValid,
    unableToSendStatmentsReasonOtherValid,
  ]);

  const closedDateFormField = (() => {
    if (hasStatementsForMultipleAccounts) {
      return (
        <CheckboxField
          name="unable_to_provide_statements_reason_close_dates"
          label={intl.formatMessage({
            id: "funding-application: air bank statement form select closed accounts",
            defaultMessage: "Select the closed accounts",
          })}
          options={(bankAccountMasks ?? []).map((mask) => ({
            name: `unable_to_provide_statements_reason_close_dates-${mask}`,
            checked: Boolean(unableToProvideStatementsReasonCloseDate?.find((item) => item.accountMask === mask)),
            label: intl.formatMessage(
              {
                id: "funding-application: air bank statement form select account mask",
                defaultMessage: "{bankName} account ending in {mask}",
              },
              {
                bankName: bankName ? bankName : intl.formatMessage(AIR_MESSAGES.bankStatementDefaultBankName),
                mask,
              }
            ),
            value: mask,
            onChange: (checked) => {
              if (checked) {
                form.setValue("unable_to_provide_statements_reason_close_dates", [
                  ...(unableToProvideStatementsReasonCloseDate ?? []),
                  { accountMask: mask, closeDate: null },
                ]);
              } else {
                form.setValue(
                  "unable_to_provide_statements_reason_close_dates",
                  (unableToProvideStatementsReasonCloseDate ?? []).filter((item) => item.accountMask !== mask)
                );
              }
            },
            associatedField: (
              <DatePickerField
                name={`unable_to_provide_statements_reason_close_dates-${mask}`}
                label={intl.formatMessage(
                  {
                    id: "funding-application: air bank statement form when was account closed",
                    defaultMessage: "On what date was it closed?",
                  },
                  { mask }
                )}
                shouldDisableDate={isFutureDate}
                clearable
                onChange={(date) => {
                  form.setValue(
                    "unable_to_provide_statements_reason_close_dates",
                    (unableToProvideStatementsReasonCloseDate ?? []).map((item) =>
                      item.accountMask === mask ? { accountMask: mask, closeDate: date } : item
                    )
                  );
                }}
              />
            ),
          }))}
        />
      );
    }

    return (
      <Flex>
        <DatePickerField
          name="unable_to_provide_statements_reason_close_date"
          label={intl.formatMessage({
            id: "funding-application: air bank statement unable to provide reason closed label",
            defaultMessage: "Roughly, when was the account closed?",
          })}
          shouldDisableDate={isFutureDate}
          onChange={(date) => {
            if (date) {
              form.setValue("unable_to_provide_statements_reason_close_dates", [
                {
                  accountMask: bankAccountMasks?.[0] ?? null,
                  closeDate: date,
                },
              ]);
            } else {
              form.setValue("unable_to_provide_statements_reason_close_dates", null);
            }
          }}
          clearable
        />
      </Flex>
    );
  })();

  return (
    <Flex direction="column" gap="8">
      <RadioField
        label={intl.formatMessage(MESSAGES.bankStatementConfirmProvidedText)}
        helperText={intl.formatMessage(MESSAGES.bankStatementConfirmProvidedTextHint)}
        type="block"
        size="small"
        name="bank-statements-confirm-provided"
        options={[
          { label: intl.formatMessage(MESSAGES.bankStatementConfirmProvidedYesText), value: true },
          { label: intl.formatMessage(MESSAGES.bankStatementConfirmProvidedNoText), value: false },
        ]}
        onChange={(value) => {
          setUnableToProvideStatements(!value);
          form.setValue("unable_to_provide_statements_reason", null);
          form.setValue("unable_to_provide_statements_reason_other", null);
          form.setValue("unable_to_provide_statements_reason_open_date", null);
          form.setValue("unable_to_provide_statements_reason_close_dates", null);
        }}
        defaultValue={null}
      />
      {unableToProvideStatements ? (
        <>
          <DropdownField
            name="unable_to_provide_statements_reason"
            options={unableToProvideStatementsOptions}
            initialValue={unableToProvideStatementsReason ?? undefined}
            label={intl.formatMessage(MESSAGES.bankStatementWhyUnableToProvideCheckboxLabel)}
            onChange={(id) => {
              if (id) {
                form.setValue("unable_to_provide_statements_reason", id);
              }
              form.setValue("unable_to_provide_statements_reason_other", null);
              form.setValue("unable_to_provide_statements_reason_open_date", null);
              form.setValue("unable_to_provide_statements_reason_close_dates", null);
            }}
          />
          {unableToProvideStatementsReason === "other" ? (
            <TextAreaField
              name="unable_to_provide_statements_reason_other"
              label={
                hasStatementsForMultipleAccounts
                  ? intl.formatMessage(
                      {
                        id: "funding-application: air reconnect bank statement unable to provide text area label",
                        defaultMessage:
                          "Please explain why you are unable to provide statements for your {accountMasks, plural, =1 {account} other {accounts}}",
                      },
                      {
                        accountMasks: getBankAccountMasksAsString(intl, bankAccountMasks ?? []),
                      }
                    )
                  : intl.formatMessage(
                      {
                        id: "funding-application: air bank statement unable to provide textarea label",
                        defaultMessage:
                          "Please explain why you are unable to provide statements for account {bankAccountNum}",
                      },
                      {
                        bankAccountNum: bankAccountMasks && bankAccountMasks.length === 1 ? bankAccountMasks[0] : "",
                      }
                    ) // Todo: Remove when translations are added
              }
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                form.setValue("unable_to_provide_statements_reason_other", e.target.value)
              }
            />
          ) : unableToProvideStatementsReason !== null ? (
            <>
              <Flex justify="space-between">
                {unableToProvideStatementsReason !== "closed" ? (
                  <Flex>
                    <DatePickerField
                      name="unable_to_provide_statements_reason_open_date"
                      label={
                        hasStatementsForMultipleAccounts
                          ? intl.formatMessage(MESSAGES.bankStatementUnableToProvideReasonOpenedTooRecentlyLabel, {
                              bankAccountMasks: bankAccountMasks?.length,
                            })
                          : intl.formatMessage({
                              id: "funding-application: air bank statement unable to provide reason opened too recently label",
                              defaultMessage: "Roughly, when was the account opened?",
                            }) // Todo: Remove when translations are added
                      }
                      clearable={false}
                      shouldDisableDate={isFutureDate}
                      onChange={(date) => {
                        form.setValue("unable_to_provide_statements_reason_open_date", date);
                      }}
                    />
                  </Flex>
                ) : null}
                {unableToProvideStatementsReason !== "opened_too_recently" ? closedDateFormField : null}
              </Flex>
            </>
          ) : null}
        </>
      ) : null}
    </Flex>
  );
};
